import React, { useState } from 'react';
import { registerCandidate } from '../../services/user/api';
import '../../styles/user/addCandidatePage.css';

const AddCandidatePage = () => {
  const [candidateData, setCandidateData] = useState({
    fullname: '',
    email: '',
    dateOfBirth: '',
    mobileNumber: '',
  });

  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [redirectTimeout, setRedirectTimeout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleCandidateChange = (e) => {
    const { name, value } = e.target;
    setCandidateData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFieldErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const validateFields = () => {
    const errors = {};

    // Fullname validation
    if (!candidateData.fullname?.trim()) {
      errors.fullname = 'Full name is required';
    } else if (!/^[A-Za-z ]+$/.test(candidateData.fullname)) {
      errors.fullname = 'Full name must contain only letters and spaces';
    } else if (candidateData.fullname.length > 40) {
      errors.fullname = 'Full name should not exceed 40 characters';
    }

    // Email validation
    if (!candidateData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._%+-]+@gmail\.com$/.test(candidateData.email)) {
      errors.email = 'Email format is invalid. Only @gmail.com is allowed';
    }

    // Date of Birth validation
    if (!candidateData.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
    } else {
      const [year, month, day] = candidateData.dateOfBirth.split('-');
      const dob = new Date(year, month - 1, day);
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      const dayDiff = today.getDate() - dob.getDate();
      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }

      if (age < 18) {
        errors.dateOfBirth = 'Age must be above 18 years';
      }
    }

    // Mobile number validation
    if (!candidateData.mobileNumber?.trim()) {
      errors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(candidateData.mobileNumber)) {
      errors.mobileNumber = 'Mobile number must be exactly 10 digits';
    } else if (/^0/.test(candidateData.mobileNumber)) {
      errors.mobileNumber = 'Mobile number should not start with 0';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;

    setLoading(true);
    try {
      const [year, month, day] = candidateData.dateOfBirth.split('-');
      const formattedData = {
        ...candidateData,
        dateOfBirth: `${day}-${month}-${year}`, // keep format for backend if needed
      };

      const response = await registerCandidate(formattedData);

      if (
        (response?.status === 200 || response?.status === 201) &&
        response?.data?.status === true
      ) {
        setSubmissionStatus('success');
      } else {
        setSubmissionStatus('error');
        setErrorMessage(response?.data?.message || 'Something went wrong.');
      }
    } catch (error) {
      console.error('Registration Error:', error);
      const msg = error?.response?.data?.message || 'Server error occurred. Please try again.';
      if (msg.toLowerCase().includes('email')) {
        setFieldErrors((prev) => ({
          ...prev,
          email: 'This email already exists. Please use a different email.',
        }));
        setSubmissionStatus(null);
      } else {
         setSubmissionStatus('error');
         setErrorMessage(error?.response?.data?.message || 'This email already exists. Please use a different email.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSubmissionStatus(null);
    setErrorMessage('');
    if (redirectTimeout) {
      clearTimeout(redirectTimeout);
      setRedirectTimeout(null);
    }

    if (submissionStatus === 'success') {
    setCandidateData({
      fullname: '',
      email: '',
      dateOfBirth: '',
      mobileNumber: '',
    });
    setFieldErrors({});
  }
  };

  return (
    <>
      <p className="profile-header">Create Login Credentials for a Candidate</p>

      <div className="add-candidate-container">
        {submissionStatus === 'success' && (
          <div className="modal-overlay">
            <div className="add-success-modal">
              <h3>Candidate registered successfully!</h3>
              <button className="ok-btn" onClick={handleCloseModal}>
                OK
              </button>
            </div>
          </div>
        )}

        {submissionStatus === 'error' && (
          <div className="modal-overlay">
            <div className="add-fail-modal">
              <h3>❌ Registration Failed</h3>
              <p>{errorMessage}</p>
              <button className="ok-btn" onClick={handleCloseModal}>
                OK
              </button>
            </div>
          </div>
        )}

        <div className="user-form-card">
          <div className="form-groups">
            <label>Candidate Name *</label>
            <input
              type="text"
              name="fullname"
              value={candidateData.fullname}
              onChange={handleCandidateChange}
              placeholder="Enter Candidate Name"
            />
            {fieldErrors.fullname && <p className="field-error">{fieldErrors.fullname}</p>}
          </div>

          <div className="form-groups">
            <label>Enter Candidate Email *</label>
            <input
              type="email"
              name="email"
              value={candidateData.email}
              onChange={handleCandidateChange}
              placeholder="Email Address"
              className={fieldErrors.email ? 'input-error' : ''}
            />
            {fieldErrors.email && (
               <p className="field-error">{fieldErrors.email}</p>
              )}
          </div>

          <div className="form-groups">
            <label>Date of birth *</label>
            <input
              type="date"
              name="dateOfBirth"
              value={candidateData.dateOfBirth}
              onChange={handleCandidateChange}
            />
            {fieldErrors.dateOfBirth && <p className="field-error">{fieldErrors.dateOfBirth}</p>}
          </div>

          <div className="form-groups">
            <label>Mobile Number *</label>
            <input
              type="text"
              name="mobileNumber"
              value={candidateData.mobileNumber}
              onChange={handleCandidateChange}
              placeholder="Mobile number"
            />
            {fieldErrors.mobileNumber && <p className="field-error">{fieldErrors.mobileNumber}</p>}
          </div>

          <button className="submit-btn" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Registering...' : 'Register Candidate'}
          </button>
        </div>
      </div>
    </>
  );
};

export default AddCandidatePage;
